import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Text,
  Paper,
  Radio,
  Checkbox,
  Button,
  Flex,
  Stack,
  Badge,
  Center,
  Divider,
  Group,
  Progress,
  Loader,
  RingProgress,
  SimpleGrid,
  ThemeIcon,
  Tooltip,
  Collapse,
  Modal,
} from "@mantine/core";
import {
  IconCheck,
  IconClock,
  IconAlertCircle,
  IconMessageCircle,
  IconAlertTriangle,
  IconQuestionMark,
  IconChevronDown,
  IconChevronUp,
  IconArrowLeft,
  IconArrowRight,
} from "@tabler/icons-react";

import type {
  Questionnaire,
  FullGradingResult,
  QuestionGradingResult,
  GradeCategory,
  Question,
  Answer,
  ScaleAnswer,
  PolicyAnswer,
  MultiSelectAnswer,
} from "@/utils/gradingEngine.types";
import { gradeFullQuestionnaire } from "@/utils/gradingEngine";

import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { getQuestionnaireId, getQuestionnaireLabel, type QuestionnaireId } from "@/utils/questionnaireMapping";

// ─── Import all questionnaires ───────────────
import q1 from "@/data/questionnaires/questionnaire_1.json";
import q2 from "@/data/questionnaires/questionnaire_2.json";
import q3 from "@/data/questionnaires/questionnaire_3.json";
import q4 from "@/data/questionnaires/questionnaire_4.json";
import q5 from "@/data/questionnaires/questionnaire_5.json";
import q6 from "@/data/questionnaires/questionnaire_6.json";

const QUESTIONNAIRE_MAP: Record<string, Questionnaire> = {
  [q1.id]: q1 as unknown as Questionnaire,
  [q2.id]: q2 as unknown as Questionnaire,
  [q3.id]: q3 as unknown as Questionnaire,
  [q4.id]: q4 as unknown as Questionnaire,
  [q5.id]: q5 as unknown as Questionnaire,
  [q6.id]: q6 as unknown as Questionnaire,
};

// ─── Props ───────────────────────────────────

type QuestionnaireTabProps = {
  matchId: string;
  collaboration: {
    id: string;
    requesterRole?: string | null;
    expectedPartnerRole?: string | null;
    requesterCompanyId: string;
    receiverCompanyId: string;
  };
  userCompanyId: string;
};

// ─── Backend response shape ──────────────────

type QuestionnaireResponseData = {
  questionnaireId: string | null;
  myAnswers: Record<string, Answer> | null;
  mySubmittedAt: string | null;
  partnerSubmittedAt: string | null;
  bothSubmitted: boolean;
  partnerAnswers: Record<string, Answer> | null;
  gradingResult: unknown | null;
};

// ─── Grade Category Helpers ──────────────────

const GRADE_CONFIG: Record<GradeCategory, { color: string; label: string; icon: React.ReactNode; tooltip: string }> = {
  alignment: {
    color: "blue",
    label: "Alignment",
    icon: <IconCheck size={16} />,
    tooltip: "Areas of strong agreement between both parties",
  },
  discussion: {
    color: "orange",
    label: "Discussion",
    icon: <IconMessageCircle size={16} />,
    tooltip: "Topics requiring further conversation to align",
  },
  key_decision: {
    color: "red",
    label: "Key Decision",
    icon: <IconAlertTriangle size={16} />,
    tooltip: "Critical differences needing resolution",
  },
  clarification: {
    color: "gray",
    label: "Clarification",
    icon: <IconQuestionMark size={16} />,
    tooltip: "Areas lacking sufficient information",
  },
};

// ─── Component ───────────────────────────────

export default function QuestionnaireTab({
  matchId,
  collaboration,
  userCompanyId: _userCompanyId,
}: QuestionnaireTabProps) {
  // ─── Resolve questionnaire from role mapping ─
  const resolvedQuestionnaireId = useMemo<QuestionnaireId | null>(() => {
    try {
      if (!collaboration.requesterRole || !collaboration.expectedPartnerRole) return null;
      return getQuestionnaireId(collaboration.requesterRole, collaboration.expectedPartnerRole);
    } catch {
      return null;
    }
  }, [collaboration.requesterRole, collaboration.expectedPartnerRole]);

  const questionnaire = resolvedQuestionnaireId ? (QUESTIONNAIRE_MAP[resolvedQuestionnaireId] ?? null) : null;

  // ─── State ─────────────────────────────────
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responseData, setResponseData] = useState<QuestionnaireResponseData | null>(null);
  const [gradingResult, setGradingResult] = useState<FullGradingResult | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const gradingTriggered = useRef(false);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  // ─── Fetch existing responses on mount ─────
  const fetchResponses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await trpc.collaboration.getQuestionnaireResponses.query({ matchId });
      setResponseData(data as QuestionnaireResponseData);
      // If user has previously submitted answers, populate them
      if (data.myAnswers) {
        setAnswers(data.myAnswers as unknown as Record<string, Answer>);
      }
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  // ─── Open modal automatically if not submitted ─
  useEffect(() => {
    if (!loading && responseData && !responseData.mySubmittedAt && resolvedQuestionnaireId) {
      setCurrentSectionIndex(0);
      setModalOpen(true);
    }
  }, [loading, responseData, resolvedQuestionnaireId]);

  // ─── Auto-grade when both submitted ────────
  useEffect(() => {
    if (
      !responseData?.bothSubmitted ||
      !responseData?.myAnswers ||
      !responseData?.partnerAnswers ||
      !questionnaire ||
      gradingTriggered.current
    )
      return;

    // If server already has a grading result, use it
    if (responseData.gradingResult) {
      setGradingResult(responseData.gradingResult as FullGradingResult);
      return;
    }

    // Run grading on the frontend
    gradingTriggered.current = true;
    const isRequester = collaboration.requesterCompanyId === _userCompanyId;

    const answersA = isRequester
      ? (responseData.myAnswers as Record<string, Answer>)
      : (responseData.partnerAnswers as Record<string, Answer>);
    const answersB = isRequester
      ? (responseData.partnerAnswers as Record<string, Answer>)
      : (responseData.myAnswers as Record<string, Answer>);

    try {
      const result = gradeFullQuestionnaire(questionnaire, answersA, answersB);
      setGradingResult(result);

      // Upload result to backend (fire-and-forget)
      trpc.collaboration.saveGradingResult
        .mutate({ matchId, gradingResult: result as unknown as Record<string, unknown> })
        .catch((err) => console.error("Failed to save grading result:", err));
    } catch (err) {
      console.error("Grading engine error:", err);
    }
  }, [responseData, questionnaire, matchId, collaboration.requesterCompanyId, _userCompanyId]);

  // ─── Toggle section expansion ─────────────
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  // ─── Derived state ────────────────────────
  const sections = questionnaire?.sections ?? [];
  const qId = questionnaire?.id ?? "";
  const isSubmitted = !!responseData?.mySubmittedAt;
  const partnerSubmitted = !!responseData?.partnerSubmittedAt;
  const bothSubmitted = !!responseData?.bothSubmitted;

  const totalQuestionCount = useMemo(() => sections.reduce((sum, s) => sum + s.questions.length, 0), [sections]);
  const requiredQuestionIds = useMemo(
    () => sections.flatMap((s) => s.questions.filter((q) => q.required).map((q) => q.id)),
    [sections]
  );
  const answeredCount = Object.keys(answers).length;
  const allRequiredAnswered =
    requiredQuestionIds.length > 0 ? requiredQuestionIds.every((id) => answers[id]) : totalQuestionCount > 0;

  // ─── Answer Handlers ─────────────────────

  const setAnswer = useCallback((answer: Answer) => {
    setAnswers((prev) => ({ ...prev, [answer.questionId]: answer }));
  }, []);

  const handleScaleChange = useCallback(
    (questionId: string, selectedOptionId: string) => {
      setAnswer({ questionId, selectedOptionId } as ScaleAnswer);
    },
    [setAnswer]
  );

  const handlePolicyChange = useCallback(
    (questionId: string, selectedOptionId: string) => {
      setAnswer({ questionId, selectedOptionId } as PolicyAnswer);
    },
    [setAnswer]
  );

  const handleMultiSelectToggle = useCallback((questionId: string, optionId: string) => {
    setAnswers((prev) => {
      const current = prev[questionId] as MultiSelectAnswer | undefined;
      const currentIds = current?.selectedOptionIds ?? [];
      const newIds = currentIds.includes(optionId)
        ? currentIds.filter((id) => id !== optionId)
        : [...currentIds, optionId];
      return { ...prev, [questionId]: { questionId, selectedOptionIds: newIds } as MultiSelectAnswer };
    });
  }, []);

  // ─── Submit Handler ──────────────────────

  const handleSubmit = useCallback(async () => {
    if (!questionnaire || !allRequiredAnswered || !qId) return;

    setSubmitting(true);
    try {
      await trpc.collaboration.submitQuestionnaire.mutate({
        matchId,
        questionnaireId: qId,
        answers,
      });
      successNotification({
        title: "Questionnaire Submitted",
        message: "Your responses have been recorded successfully.",
      });
      setModalOpen(false);
      // Refresh response data from server
      await fetchResponses();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSubmitting(false);
    }
  }, [questionnaire, allRequiredAnswered, qId, matchId, answers, fetchResponses]);

  // ─── Question Renderer ───────────────────

  const renderQuestion = (question: Question, readOnly = false) => {
    const answer = answers[question.id];

    return (
      <Paper key={question.id} withBorder radius="md" p="lg" mb="md">
        {/* Question header */}
        <Flex justify="space-between" align="center" mb="sm">
          <Text fw={500} fz="md" style={{ flex: 1 }}>
            {question.text}
            {question.required && (
              <Text component="span" c="red" ml={4}>
                *
              </Text>
            )}
          </Text>
          <Badge variant="light" size="sm" color="gray" ml="sm" style={{ flexShrink: 0 }}>
            {question.type === "scale" ? "Scale" : question.type === "policy" ? "Single Select" : "Multi Select"}
          </Badge>
        </Flex>

        {/* Scale */}
        {question.type === "scale" && (
          <Radio.Group
            value={(answer as ScaleAnswer | undefined)?.selectedOptionId ?? ""}
            onChange={(val) => !readOnly && handleScaleChange(question.id, val)}
          >
            <Stack gap="sm" mt="md">
              {question.options.map((opt) => (
                <Box key={opt.id} py={4}>
                  <Radio
                    value={opt.id}
                    label={opt.label}
                    disabled={readOnly}
                    styles={{
                      label: { cursor: readOnly ? "default" : "pointer" },
                      radio: { cursor: readOnly ? "default" : "pointer" },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Radio.Group>
        )}

        {/* Policy */}
        {question.type === "policy" && (
          <Radio.Group
            value={(answer as PolicyAnswer | undefined)?.selectedOptionId ?? ""}
            onChange={(val) => !readOnly && handlePolicyChange(question.id, val)}
          >
            <Stack gap="sm" mt="md">
              {question.options.map((opt) => (
                <Box key={opt.id} py={4}>
                  <Radio
                    value={opt.id}
                    label={opt.label}
                    disabled={readOnly}
                    styles={{
                      label: {
                        cursor: readOnly ? "default" : "pointer",
                        "&[data-disabled]": { color: "var(--mantine-color-slate-6)" },
                      },
                      radio: {
                        cursor: readOnly ? "default" : "pointer",
                        "&[data-disabled]": { borderColor: "var(--mantine-color-slate-4)" },
                      },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Radio.Group>
        )}

        {/* Multi-Select */}
        {question.type === "multi_select" && (
          <Stack gap="sm" mt="md">
            {question.options.map((opt) => {
              const selected = (answer as MultiSelectAnswer | undefined)?.selectedOptionIds?.includes(opt.id) ?? false;
              return (
                <Box key={opt.id} py={4}>
                  <Checkbox
                    checked={selected}
                    onChange={() => !readOnly && handleMultiSelectToggle(question.id, opt.id)}
                    label={opt.label}
                    disabled={readOnly}
                    styles={{
                      label: { cursor: readOnly ? "default" : "pointer" },
                      input: { cursor: readOnly ? "default" : "pointer" },
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>
    );
  };

  // ─── Loading state ───────────────────────

  if (loading) {
    return (
      <Center py="xl" mih="40vh">
        <Loader size="lg" />
      </Center>
    );
  }

  // ─── No roles assigned (no questionnaire can be resolved) ──

  if (!resolvedQuestionnaireId || !questionnaire) {
    return (
      <Center py="xl" mih="40vh">
        <Stack align="center" gap="sm">
          <IconAlertCircle size={48} color="var(--mantine-color-yellow-6)" />
          <Text fz="lg" fw={500} c="slate.7">
            No Questionnaire Available
          </Text>
          <Text fz="sm" c="slate.5" ta="center" maw={400}>
            Collaboration roles have not been assigned yet. The questionnaire will appear once both parties' roles are
            determined.
          </Text>
        </Stack>
      </Center>
    );
  }

  // ─── Both submitted → show grading results ──

  if (bothSubmitted) {
    const summary = gradingResult?.summary;
    const resultsBySection = gradingResult?.results?.reduce(
      (acc, r) => {
        if (!acc[r.sectionId]) acc[r.sectionId] = [];
        acc[r.sectionId].push(r);
        return acc;
      },
      {} as Record<string, QuestionGradingResult[]>
    );

    return (
      <Box>
        {/* Header */}
        <Paper withBorder radius="md" p="lg" mb="lg">
          <Flex justify="space-between" align="center" wrap="wrap" gap="sm">
            <Box>
              <Text fw={500} fz="lg">
                {getQuestionnaireLabel(resolvedQuestionnaireId)}
              </Text>
              {questionnaire.description && (
                <Text fz="sm" c="slate.6" mt={4}>
                  {questionnaire.description}
                </Text>
              )}
            </Box>
            <Badge variant="light" color="blue" size="md">
              Both Parties Submitted
            </Badge>
          </Flex>
        </Paper>

        {/* Grading Summary */}
        {gradingResult && summary ? (
          <>
            <Paper withBorder radius="md" p="lg" mb="lg">
              <Text fw={600} fz="lg" mb="md">
                Compatibility Summary
              </Text>
              <Flex gap="xl" align="center" wrap="wrap">
                {/* Ring progress */}
                <RingProgress
                  size={140}
                  thickness={14}
                  roundCaps
                  sections={[
                    { value: (summary.alignment / summary.total) * 100, color: "blue" },
                    { value: (summary.discussion / summary.total) * 100, color: "orange" },
                    { value: (summary.key_decision / summary.total) * 100, color: "red" },
                    { value: (summary.clarification / summary.total) * 100, color: "gray" },
                  ].filter((s) => s.value > 0)}
                  label={
                    <Stack gap={0} align="center" justify="center" h="100%">
                      <Text fw={700} fz="xl" lh={1}>
                        {summary.total}
                      </Text>
                      <Text fz={10} c="dimmed" mt={4} tt="uppercase" fw={600}>
                        questions
                      </Text>
                    </Stack>
                  }
                />

                {/* Category breakdown */}
                <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="lg" style={{ flex: 1 }}>
                  {(["alignment", "discussion", "key_decision", "clarification"] as GradeCategory[]).map((cat) => {
                    const config = GRADE_CONFIG[cat];
                    const count = summary[cat];
                    const pct = summary.total > 0 ? Math.round((count / summary.total) * 100) : 0;
                    return (
                      <Tooltip key={cat} label={config.tooltip} withArrow multiline w={200}>
                        <Paper withBorder radius="md" p="md" style={{ textAlign: "center" }}>
                          <ThemeIcon variant="light" color={config.color} size="lg" radius="xl" mx="auto" mb="xs">
                            {config.icon}
                          </ThemeIcon>
                          <Text fw={700} fz="xl">
                            {count}
                          </Text>
                          <Text fz="xs" c="slate.5">
                            {config.label} ({pct}%)
                          </Text>
                        </Paper>
                      </Tooltip>
                    );
                  })}
                </SimpleGrid>
              </Flex>
            </Paper>

            {/* Per-section breakdown */}
            {sections.map((section) => {
              const sectionResults = resultsBySection?.[section.id] ?? [];
              const isExpanded = expandedSections.has(section.id);
              const sectionSummary = sectionResults.reduce(
                (acc, r) => {
                  acc[r.category] = (acc[r.category] || 0) + 1;
                  return acc;
                },
                {} as Record<string, number>
              );

              return (
                <Paper key={section.id} withBorder radius="md" mb="md">
                  {/* Section header — clickable */}
                  <Flex
                    align="center"
                    justify="space-between"
                    p="md"
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleSection(section.id)}
                  >
                    <Group gap="sm">
                      <Text fw={500} fz="md">
                        {section.title}
                      </Text>
                      <Group gap={4}>
                        {(["alignment", "discussion", "key_decision", "clarification"] as GradeCategory[]).map(
                          (cat) => {
                            const count = sectionSummary[cat] || 0;
                            if (count === 0) return null;
                            return (
                              <Tooltip key={cat} label={`${GRADE_CONFIG[cat].label}: ${count}`}>
                                <Badge size="sm" variant="light" color={GRADE_CONFIG[cat].color}>
                                  {count}
                                </Badge>
                              </Tooltip>
                            );
                          }
                        )}
                      </Group>
                    </Group>
                    {isExpanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                  </Flex>

                  {/* Section questions */}
                  <Collapse in={isExpanded}>
                    <Divider />
                    <Box p="md">
                      {sectionResults.map((result) => {
                        const config = GRADE_CONFIG[result.category];
                        return (
                          <Paper key={result.questionId} withBorder radius="md" p="md" mb="sm">
                            <Flex justify="space-between" align="center" mb="xs">
                              <Text fz="sm" fw={500} style={{ flex: 1 }}>
                                {result.questionText}
                              </Text>
                              <Badge variant="light" color={config.color} size="sm" ml="sm" style={{ flexShrink: 0 }}>
                                {config.label}
                              </Badge>
                            </Flex>
                            <Flex gap="xs">
                              <Badge variant="outline" size="xs" color="gray">
                                {result.questionType === "scale"
                                  ? "Scale"
                                  : result.questionType === "policy"
                                    ? "Single Select"
                                    : "Multi Select"}
                              </Badge>
                            </Flex>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </>
        ) : (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Loader size="md" />
              <Text fz="sm" c="slate.5">
                Computing compatibility results...
              </Text>
            </Stack>
          </Center>
        )}
      </Box>
    );
  }

  // ─── User already submitted, waiting for partner ──

  if (isSubmitted && !partnerSubmitted) {
    return (
      <Box>
        <Paper withBorder radius="md" p="lg" mb="lg">
          <Flex justify="space-between" align="center" wrap="wrap" gap="sm">
            <Box>
              <Text fw={500} fz="lg">
                {getQuestionnaireLabel(resolvedQuestionnaireId)}
              </Text>
              {questionnaire.description && (
                <Text fz="sm" c="slate.6" mt={4}>
                  {questionnaire.description}
                </Text>
              )}
            </Box>
            <Group gap="sm">
              <Badge variant="light" color="blue" size="md">
                Submitted
              </Badge>
            </Group>
          </Flex>
        </Paper>

        <Paper withBorder radius="md" p="xl">
          <Center>
            <Stack align="center" gap="sm">
              <IconClock size={48} color="var(--mantine-color-primary-6)" />
              <Text fw={500} fz="lg">
                Waiting for Partner
              </Text>
              <Text fz="sm" c="slate.6" ta="center" maw={400}>
                Your responses have been recorded. Results will be available once your collaboration partner submits
                their questionnaire.
              </Text>
            </Stack>
          </Center>
        </Paper>

        {/* Show submitted answers read-only */}
        <Text fw={500} fz="md" mt="xl" mb="md" c="slate.6">
          Your Submitted Responses
        </Text>
        {sections.map((section) => (
          <Box key={section.id} mb="lg">
            <Text fw={500} fz="lg" mb="md">
              {section.title}
            </Text>
            {section.questions.map((q) => renderQuestion(q, true))}
          </Box>
        ))}
      </Box>
    );
  }

  // ─── Active questionnaire (user hasn't submitted yet) ──

  const progressPct = totalQuestionCount > 0 ? Math.round((answeredCount / totalQuestionCount) * 100) : 0;
  const currentSection = sections[currentSectionIndex];
  const isFirstSection = currentSectionIndex === 0;
  const isLastSection = currentSectionIndex === sections.length - 1;

  // Per-section answer counts
  const currentSectionQuestions = currentSection?.questions ?? [];
  const currentSectionAnswered = currentSectionQuestions.filter((q) => answers[q.id]).length;
  const currentSectionTotal = currentSectionQuestions.length;

  const scrollToTop = () => {
    setTimeout(() => {
      modalBodyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const goToNextSection = () => {
    if (!isLastSection) {
      setCurrentSectionIndex((prev) => prev + 1);
      scrollToTop();
    }
  };

  const goToPrevSection = () => {
    if (!isFirstSection) {
      setCurrentSectionIndex((prev) => prev - 1);
      scrollToTop();
    }
  };

  const openModal = () => {
    setCurrentSectionIndex(0);
    setModalOpen(true);
  };

  // ─── Questionnaire form content (reused inside modal) ──

  const sectionProgressPct =
    currentSectionTotal > 0 ? Math.round((currentSectionAnswered / currentSectionTotal) * 100) : 0;

  const questionnaireForm = (
    <Box ref={modalBodyRef}>
      {/* ── Combined header + section card ── */}
      <Paper withBorder radius="md" p="xl" mb="lg">
        <Text fw={500} fz={28} c="slate.8" lh={1.2}>
          {getQuestionnaireLabel(resolvedQuestionnaireId)}
        </Text>
        {questionnaire.description && (
          <Text fz="sm" c="slate.5" mt={8}>
            {questionnaire.description}
          </Text>
        )}

        <Divider my="lg" />

        {currentSection && (
          <>
            <Text fw={500} fz="lg" c="slate.8">
              {currentSection.title}
            </Text>
            <Flex justify="space-between" align="center" mt={8} mb={8}>
              <Text fz="sm" c="slate.5">
                Section {currentSectionIndex + 1} of {sections.length}
              </Text>
              <Text fz="sm" fw={600} c="primary">
                {progressPct}% Complete
              </Text>
            </Flex>
            <Progress value={sectionProgressPct} size={10} radius="xl" color="primary" aria-label="Section progress" />
          </>
        )}
      </Paper>

      {/* ── Questions for current section ── */}
      {currentSectionQuestions.map((q) => renderQuestion(q))}

      {/* ── Navigation buttons ── */}
      <Flex justify="space-between" align="center" mt="lg" mb="xl">
        <Button
          variant="default"
          leftSection={<IconArrowLeft size={16} />}
          onClick={goToPrevSection}
          disabled={isFirstSection}
          size="md"
        >
          Previous
        </Button>

        <Group gap="sm">
          {isLastSection && (
            <Button variant="outline" color="primary" size="md" onClick={() => setModalOpen(false)}>
              Skip for now
            </Button>
          )}

          {isLastSection ? (
            <Button disabled={!allRequiredAnswered} loading={submitting} onClick={handleSubmit} size="md">
              Submit Questionnaire
            </Button>
          ) : (
            <Button rightSection={<IconArrowRight size={16} />} onClick={goToNextSection} size="md">
              Next
            </Button>
          )}
        </Group>
      </Flex>
    </Box>
  );

  return (
    <>
      {/* ── Fullscreen modal for unfilled questionnaire ── */}
      <Modal
        opened={modalOpen}
        onClose={() => {}}
        fullScreen
        withCloseButton={false}
        closeOnEscape={false}
        closeOnClickOutside={false}
        styles={{
          body: { padding: "24px 48px", maxWidth: 960, margin: "0 auto" },
          header: { display: "none" },
        }}
      >
        {questionnaireForm}
      </Modal>

      {/* ── Tab content when modal is closed (skipped) ── */}
      <Center py="xl" mih="40vh">
        <Stack align="center" gap="sm">
          <IconAlertCircle size={48} color="var(--mantine-color-primary-6)" />
          <Text fz="lg" fw={500} c="slate.7">
            Questionnaire Not Completed
          </Text>
          <Text fz="sm" c="slate.5" ta="center" maw={400}>
            Please complete the questionnaire to proceed with the collaboration assessment.
          </Text>
          <Button mt="md" onClick={openModal}>
            Open Questionnaire
          </Button>
        </Stack>
      </Center>
    </>
  );
}
